import EmailIcon from '@mui/icons-material/Drafts';
import SaveIcon from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useCallback, useState } from 'react';

import { MemberEmailForm, ProgressButton } from 'components';
import {
	getCarbonCopiesForCreate,
	getCarbonCopiesForDeletion,
	isMissingData
} from 'routes/onboard/member_email/helpers';
import {
	useCreateEmailCarbonCopyMutation,
	useDeleteEmailCarbonCopyMutation,
	useEditMemberNotificationMutation,
	useGetMemberNotificationByIdQuery
} from 'store/api/version-api.slice';

import ModalTestEmail from './ModalTestEmail';
import { styles } from './styles';

const EditNotificationEmailDialog = ({ dialogs, handleDialogClose, editEmailForm, handleEditFormValueChange }) => {
	const [toggleTestEmail, setToggleTestEmail] = useState(false);

	const [editMemberNotification, { isLoading: isEditingNotification }] = useEditMemberNotificationMutation();

	const [createEmailCC, { isLoading: isCreateLoading }] = useCreateEmailCarbonCopyMutation();
	const [deleteEmailCC, { isLoading: isDeleteLoading }] = useDeleteEmailCarbonCopyMutation();

	const { data: currentEmailNotification = {}, isLoading: isGetNotificationLoading } =
		useGetMemberNotificationByIdQuery(editEmailForm.id, { skip: !editEmailForm.id });

	const handlePatchEmail = useCallback(async () => {
		const previousIds = currentEmailNotification.memberemailformcc_set.map(cc => cc.id);
		const currentIds = editEmailForm.memberemailformcc_set.map(cc => cc.id);

		const carbonCopiesIdsToDelete = getCarbonCopiesForDeletion({
			previousCCIds: previousIds,
			currentCCIds: currentIds
		});

		const carbonCopiesToCreate = getCarbonCopiesForCreate(editEmailForm.memberemailformcc_set);

		// if email has an id than we can fire update carbon copy requests in parallel
		const editPromise = editMemberNotification({ id: editEmailForm.id, payload: editEmailForm });

		const deleteCCPromises = carbonCopiesIdsToDelete.map(id => deleteEmailCC(id));
		const createCCPromises = carbonCopiesToCreate.map(cc => createEmailCC(cc));

		await Promise.allSettled([...deleteCCPromises, ...createCCPromises, editPromise]);

		handleDialogClose();
	}, [
		createEmailCC,
		currentEmailNotification.memberemailformcc_set,
		deleteEmailCC,
		editEmailForm,
		editMemberNotification,
		handleDialogClose
	]);

	const handleToggleTestEmail = () => {
		setToggleTestEmail(prev => !prev);
	};

	const isSaveLoading = isEditingNotification || isCreateLoading || isDeleteLoading;
	const isSaveDisabled =
		isGetNotificationLoading ||
		isEditingNotification ||
		isCreateLoading ||
		isDeleteLoading ||
		isMissingData(editEmailForm);

	return (
		<Dialog
			TransitionProps={{ tabIndex: '' }}
			open={dialogs.edit}
			onClose={handleDialogClose}
			maxWidth={'md'}
			fullWidth={true}
		>
			<DialogTitle>Edit Notification Email</DialogTitle>
			<DialogContent sx={styles.dialogContent}>
				<MemberEmailForm
					email_type={editEmailForm.email_type}
					email_body={editEmailForm.email_body}
					frequency={editEmailForm.frequency}
					email_subject={editEmailForm.email_subject}
					memberemailformcc_set={editEmailForm.memberemailformcc_set}
					id={editEmailForm.id}
					handleChange={handleEditFormValueChange}
				/>
			</DialogContent>
			<DialogActions sx={styles.dialogActions}>
				<Button sx={styles.btnTestEmail} onClick={handleToggleTestEmail} color="primary">
					<EmailIcon sx={{ ...styles.iconOffset, color: 'primary.main' }} /> Test Email
				</Button>
				<Box
					sx={{
						marginRight: '18px',
						display: 'flex'
					}}
				>
					<Button sx={styles.actionButtons} onClick={handleDialogClose} color="secondary">
						Cancel
					</Button>

					<ProgressButton
						isLoading={isSaveLoading}
						sx={{ flexGrow: 0 }}
						button={
							<Button
								sx={{
									...styles.actionButtons,
									...styles.saveBtn
								}}
								onClick={handlePatchEmail}
								variant="contained"
								color="primary"
								disabled={isSaveDisabled}
							>
								<SaveIcon sx={{ ...styles.iconOffset, color: '#ffffff' }} />
								Save
							</Button>
						}
						circleColor={'secondary'}
					/>
				</Box>
			</DialogActions>
			{toggleTestEmail && <ModalTestEmail handleClose={handleToggleTestEmail} data={editEmailForm} />}
		</Dialog>
	);
};

export default EditNotificationEmailDialog;
